/* eslint-disable no-empty-function */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const DEFAULT_INPUT_WIDTH = '50%';
const DEFAULT_MAX_LENGTH = 100;

@Component({
  selector: 'ps-ui-input-with-confirmation',
  template: `
    <div class="input-container h-100">
      <input
        #inputElement
        class="rounded-2"
        [type]="type"
        [value]="inputValue"
        [maxLength]="maxLength"
        [style.width]="width || '50%'"
        (click)="onInputFocus($event)"
        (keyup)="onKeyUpConfirm($event)"
      />

      <div class="actions">
        <i
          class="fa fa-check glyphicon glyphicon-ok text-primary"
          (click)="onConfirm($event)"
        ></i>
        <i
          class="fa fa-remove glyphicon glyphicon-remove text-danger"
          (click)="onCancel($event)"
        ></i>
      </div>
    </div>
  `,
  styleUrls: ['./input-with-confirmation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputWithConfirmationComponent),
      multi: true,
    },
  ],
})
export class InputWithConfirmationComponent<T = string>
  implements ControlValueAccessor, AfterViewInit
{
  @Input() public type: 'text' | 'number' = 'text';

  @Input()
  public set value(_value: T) {
    this.writeValue(_value);
  }

  @Input() public width = DEFAULT_INPUT_WIDTH;

  @Input() public maxLength: number | string = DEFAULT_MAX_LENGTH;

  @Input() public allowKeyUp = true;

  @Output() public valueChange = new EventEmitter<T>();

  @Output() public confirm = new EventEmitter<T>();

  @Output() public cancel = new EventEmitter<void>();

  @ViewChild('inputElement')
  public inputElementRef!: ElementRef;

  /** Store the latest confirmed value */
  public inputValue: T;

  /** Store the current input value */
  private _temporaryValue: T;

  private onChange: any = () => {};
  private onTouched: any = () => {};

  @HostListener('input', ['$event.target.value'])
  public inputValueChange(_value: T) {
    this._temporaryValue = _value;
  }

  ngAfterViewInit(): void {
    this.inputElementRef.nativeElement.focus();
  }

  onConfirm(event: Event): void {
    this.inputValue = this._temporaryValue;

    this.onChange(this.inputValue);
    this.onTouched();
    this.confirm.emit(this.inputValue);
    this.valueChange.emit(this.inputValue);
    event.stopPropagation();
  }

  onCancel(event: Event): void {
    this._temporaryValue = this.inputValue;

    this.onTouched();
    this.cancel.emit();
    event.stopPropagation();
  }

  onKeyUpConfirm(event: KeyboardEvent): void {
    if (this.allowKeyUp && event.key === 'Enter') {
      this.onConfirm(event);
    }
  }

  onInputFocus(event: Event): void {
    event.stopPropagation();
  }

  // ControlValueAccessor Implementation
  writeValue(newValue: T): void {
    this.inputValue = newValue;
    this._temporaryValue = newValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.inputElementRef.nativeElement.disabled = isDisabled;
  }
}
